import {
  ConversionEventKind,
  ConversionEventTypes,
  ConversionEventValueType,
  useConversionEventsByKind,
} from '@va/dashboard/shared/conversions';

import { useTranslate } from '@va/localization';
import { MultiSelectFilter } from '@va/shared/feature-filters';
import { FilterLogicalOperators, FilterOperators } from '@va/types/filters';
import { WithRequired } from '@va/types/util';
import { createInitialScrollState, useInfiniteScroll } from '@va/util/hooks';
import { useMemo } from 'react';

type ConversionEventKindValue = (typeof ConversionEventKind)[keyof typeof ConversionEventKind];

export const useConversionEventsOptions = (kind?: ConversionEventKindValue) => {
  const translate = useTranslate();
  const hookResponse = useInfiniteScroll<
    WithRequired<ConversionEventValueType, 'id'>,
    { kind?: ConversionEventKindValue }
  >({
    fetchHook: useConversionEventsByKind,
    initialState: createInitialScrollState({ pageSize: 10, filter: kind ? { kind } : {}, shouldFetch: true }),
  });

  return useMemo(
    () => ({
      data: hookResponse.items.map((item, index) => {
        return {
          label: item.name ?? translate('labels.unknown'),
          value: item.id ?? '',
          ref: index === hookResponse.items.length - 1 ? hookResponse.lastElementRef : null,
        };
      }),
      isLoading: hookResponse.isFetchingInitial,
      error: hookResponse.error,
    }),
    [hookResponse.error, hookResponse.isFetchingInitial, hookResponse.items, hookResponse.lastElementRef, translate],
  );
};

export const useConversionEventKindFilter = (
  kind: ConversionEventKindValue = ConversionEventKind[ConversionEventTypes.WebsitePageUrl],
) => {
  const translate = useTranslate();

  return useMemo(
    () =>
      new MultiSelectFilter({
        id: `conversion-${kind}`,
        name: kind,
        apiFieldNameMapper: () => 'conversionEventId',
        label: translate(`filters.conversionEvents.${kind}`),
        operatorOptions: [FilterOperators.is, FilterOperators.isNot],
        optionsLogicalOperator: FilterLogicalOperators.or,
        inputProps: {
          useOptions: useConversionEventsOptions.bind(this, kind),
          showSearchInput: true,
        },
      }),
    [kind, translate],
  );
};
